import React from 'react';
import { motion } from 'framer-motion';
import { InfinityIcon, UserRoundCheckIcon, MessagesSquareIcon, BrainIcon, TrophyIcon, MessageCircleIcon, PlaneIcon, VideoIcon, UsersRoundIcon, BriefcaseBusinessIcon } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { CookieBanner } from '../components/CookieBanner';
import { NextStep } from '../components/NextStep';
import { academySteps } from '../data/academySteps';
import { Sparkle } from '../components/Sparkle';
import { useSeo } from '../hooks/useSeo';
const stepIcons = [InfinityIcon, UserRoundCheckIcon, MessagesSquareIcon, BrainIcon, TrophyIcon, MessageCircleIcon, PlaneIcon, VideoIcon, UsersRoundIcon, BriefcaseBusinessIcon];
export function Ablauf() {
  useSeo({
    title: 'Ablauf der Academy | Hot Sales Girls Academy',
    description: 'Schritt für Schritt zum Erfolg: So läuft die Hot Sales Girls Academy ab – von den Grundlagen im Closing über wöchentliche Live-Trainings bis zur Begleitung in deinen Remote Job.',
    path: '/ablauf-der-hot-sales-girls-academy'
  });
  return <div className="min-h-screen w-full overflow-x-hidden" data-id="element-429" data-mp="rusdprbrs2co">
      <div className="pink-panel relative" data-id="element-430" data-mp="n0xq4ct1n38b">
        <div className="relative z-10" data-id="element-431" data-mp="mzml69nv3pfm">
          <Navbar data-id="element-432" data-mp="i3o0ox5nmrkh" />

          <main className="relative w-full bg-cream px-4 pb-2 pt-6 sm:px-6 sm:pb-4 sm:pt-8" data-id="element-433" data-mp="i233ezryguix">
            <span aria-hidden="true" className="gold-glitter-layer-static" data-id="element-434" data-mp="d4kw1dtl6nd1" />
            <span aria-hidden="true" className="gold-glitter-extra" data-id="element-435" data-mp="84famfdwtx3e" />
            <div className="relative z-10 mx-auto max-w-[1300px]" data-id="element-436" data-mp="433io37kczs1">
              <header className="text-center" data-id="element-437" data-mp="43d9tu4chczb">
                <span className="mb-6 block text-[11px] font-semibold uppercase tracking-[0.25em] text-hot-500" data-id="element-438" data-mp="3v9xveybmrsoy">
                  Deine Roadmap
                </span>
                <div className="relative inline-block" data-id="element-439" data-mp="1yf1odv6nzdj6">
                  <Sparkle className="animate-sparkle-delayed bottom-1 right-[3.75rem] h-8 w-8 sm:-right-8 sm:-top-4 sm:bottom-auto sm:h-10 sm:w-10 md:bottom-1 md:right-[28%] md:top-auto lg:-right-8 lg:-top-4 lg:bottom-auto" data-id="element-440" data-mp="1tj2yg35otngn" />
                  <h1 className="font-heading mx-auto max-w-3xl text-3xl font-extrabold uppercase leading-tight text-neutral-900 sm:text-5xl" data-id="element-441" data-mp="2heo5rbcdsqjp">
                    Dein Weg zur High-Ticket Sales Expertin
                  </h1>
                </div>
                <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-neutral-600 sm:text-base" data-id="element-442" data-mp="2wa43prg316ld">
                  Das erwartet dich in den 8 Wochen – und weit darüber hinaus.
                </p>
              </header>

              {/* Design 1: durchgehende Zeitleiste links, Karten über die volle Breite. */}
              <div className="relative mx-auto mt-8 max-w-4xl sm:mt-10 lg:mt-10" data-id="element-443" data-mp="2rahm75t8146r">
                {/* Durchgehende goldene Linie */}
                <span aria-hidden="true" className="absolute bottom-8 left-5 top-8 w-px bg-gold/40 sm:left-9" data-id="element-444" data-mp="1azdq1zswmxfv" />

                <ol className="space-y-6 sm:space-y-8" data-id="element-445" data-mp="18btg89wf4fn4">
                  {academySteps.map((step, index) => {
                  const Icon = stepIcons[index];
                  return <motion.li key={step.number}
                  // Die ersten Schritte sind beim Öffnen sofort sichtbar – nur die
                  // weiter unten liegenden blenden beim Scrollen ein.
                  initial={index < 2 ? false : {
                    opacity: 0,
                    y: 16
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} viewport={{
                    once: true,
                    margin: '-60px'
                  }} transition={{
                    duration: 0.4,
                    ease: [0.23, 1, 0.32, 1]
                  }} className="relative flex items-start gap-4 sm:gap-8" data-id="element-446" data-mp="1hchzbj0o566v" data-mpi={index}>
                        {/* Nummer auf der Linie */}
                        <span className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gold/60 bg-cream shadow-[0_0_0_3px_rgba(253,251,245,1),0_2px_10px_rgba(192,154,87,0.35)] sm:h-[4.5rem] sm:w-[4.5rem]" data-id="element-447" data-mp="zujsdjeg89z">
                          <span className="font-numeral text-gold-metallic -translate-y-[0.16em] text-lg font-black leading-none sm:text-3xl" data-id="element-448" data-mp="jj0gsqxr4oc1">
                            {step.number}
                          </span>
                        </span>

                        <div className="flex-1 rounded-3xl border border-gold/20 bg-white/70 p-5 shadow-sm backdrop-blur-[2px] sm:p-8" data-id="element-449" data-mp="xdayiobbzi8l">
                          <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-hot-50" data-id="element-450" data-mp="129rl3e2vtojk">
                            <Icon className="h-5 w-5 text-hot-500" aria-hidden="true" data-id="element-451" data-mp="178mhjitrvbm5" />
                          </span>

                          <h2 className="font-heading mt-4 text-lg font-bold leading-snug text-neutral-900 sm:mt-6 sm:text-2xl" data-id="element-452" data-mp="392bw7xrcgnk6">
                            {step.title}
                          </h2>

                          <p className="mt-3 text-sm leading-relaxed text-neutral-600 sm:text-base" data-id="element-453" data-mp="1v5sh29zb5ljw">
                            {step.text}
                          </p>
                        </div>
                      </motion.li>;
                })}
                </ol>
              </div>
            </div>
          </main>

          <NextStep data-id="element-454" data-mp="1hg753jjxe6ld" />

          <Footer data-id="element-455" data-mp="1hcjbkkehdk6n" />
        </div>
      </div>
      <CookieBanner data-id="element-456" data-mp="2e56e50sc3cem" />
    </div>;
}